import * as React from 'react';
import {Link as RouterLink} from 'react-router-dom';

import {Button} from 'shells/button/button';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
// @ts-expect-error - imported from .js/.jsx file
import {BrowserIcon} from 'shells/icon/svg/browser-icon';
// @ts-expect-error - imported from .js/.jsx file
import {CalendarIcon} from 'shells/icon/svg/calendar-icon';
import {CodeIcon} from 'shells/icon/svg/code-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EmailIcon} from 'shells/icon/svg/email-icon';
// @ts-expect-error - imported from .js/.jsx file
import {GoalClickIcon} from 'shells/icon/svg/goal-click-icon';
import {Link} from 'shells/link/link';
import type {StatusBadgeProps} from 'shells/status-badge/status-badge';
import {DEFAULT_ADMIN_NO_OPTIONS_MESSAGE} from 'shells/subdomain-select/subdomain-select';
import type {SubdomainOption} from 'shells/subdomain-select/subdomain-select';
import {Body} from 'shells/typography/body';

import type {FeatureStatus, DomainFeatureStatusKey} from 'nutshell-graphql-types';

import {SubdomainSelectWrapper} from '../../core/subdomains';

import styles from './feature-statuses.module.css';

const FEATURES: Partial<
    Record<
        DomainFeatureStatusKey,
        {
            title: string;
            subtitle: string;
            icon: React.ComponentType<any>;
            canConfigureDomain: boolean;
            kba?: string;
            withSubdomainPlaceholder?: boolean;
        }
    >
> = {
    emailDeliverability: {
        title: 'Email deliverability',
        subtitle: 'Increase your reliability and skip the spam folder with DMARC.',
        icon: EmailIcon,
        canConfigureDomain: false,
        kba: 'https://support.nutshell.com/en/articles/8428873-email-marketing-how-to-set-up-dkim-spf-and-dmarc',
    },
    customClickTracking: {
        title: 'Custom click-tracking domain',
        subtitle: 'Use your domain’s reputation for better delivery.',
        icon: GoalClickIcon,
        canConfigureDomain: true,
        kba: 'https://support.nutshell.com/en/articles/8796616-marketing-how-to-set-up-custom-domains',
    },
    landingPages: {
        title: 'Landing pages',
        subtitle: 'Host landing pages on your company’s domain name',
        canConfigureDomain: true,
        icon: BrowserIcon,
    },
    schedulerAndForms: {
        title: 'Scheduler and forms',
        subtitle: 'Use your domain for your Scheduler pages',
        canConfigureDomain: true,
        icon: CalendarIcon,
    },
    ...(NutClientConfig.hasDnsRecordsForBootSnippetFF && {
        bootSnippet: {
            title: 'Snippet features',
            subtitle:
                'Use the selected subdomain for chat and visitor analytics. After you pick a subdomain, make sure to re-embed the tracking snippet to update it.',
            canConfigureDomain: true,
            icon: CodeIcon,
            withSubdomainPlaceholder: true,
        },
    }),
};

const STATUS_BADGES: Partial<Record<FeatureStatus, StatusBadgeProps>> = {
    ACTIVE: {
        level: 'success',
        text: 'Active',
    },
    NOT_ACTIVE: {
        level: 'warning',
        text: 'Needs to be activated',
    },
    NOT_ENABLED: {
        level: 'warning',
        text: 'Not enabled',
    },
    NOT_CONNECTED: {
        level: 'info--grey',
        text: 'Not connected',
    },
};

export function getFeatureStatusTitle(key: DomainFeatureStatusKey): string {
    // @ts-expect-error
    return FEATURES[key].title;
}

export function getFeatureStatusSubtitle(key: DomainFeatureStatusKey): React.ReactElement {
    // @ts-expect-error
    const subtitle = FEATURES[key].subtitle;
    // @ts-expect-error
    const kba = FEATURES[key].kba;

    return kba ? (
        <>
            <Body color='light' size='small'>
                {subtitle}
            </Body>{' '}
            {/* @ts-expect-error */}
            <Link href={kba} target='_blank' variant='primary' size='small'>
                Learn more
            </Link>
        </>
    ) : (
        <Body color='light' size='small'>
            {subtitle}
        </Body>
    );
}

export function getFeatureStatusTertiaryData(
    key: DomainFeatureStatusKey,
    subdomains: SubdomainOption[],
    selectedSubdomain: SubdomainOption | null | undefined,
    onSelectOption: (option?: SubdomainOption | null | undefined) => void,
    onAddDomain: () => void
): React.ReactElement {
    // @ts-expect-error
    return FEATURES[key].canConfigureDomain ? (
        <div className={styles['domain-select']}>
            <SubdomainSelectWrapper
                selectedOption={selectedSubdomain}
                onSelectOption={onSelectOption}
                disableUnverifiedOptions={true}
                options={subdomains}
                isClearable={true}
                // @ts-expect-error
                withSubdomainPlaceholder={FEATURES[key].withSubdomainPlaceholder}
                customNoOptionsMessage={{
                    ...DEFAULT_ADMIN_NO_OPTIONS_MESSAGE,
                    cta: (
                        <Button variant='primary' onClick={onAddDomain}>
                            Add a domain
                        </Button>
                    ),
                }}
            />
        </div>
    ) : (
        <></>
    );
}

export function getFeatureStatusBadge(
    key: DomainFeatureStatusKey,
    subdomains: SubdomainOption[],
    status: FeatureStatus
): StatusBadgeProps {
    if (key !== 'bootSnippet') {
        // @ts-expect-error
        return STATUS_BADGES[status];
    }

    if (subdomains.length === 0) {
        // @ts-expect-error
        return STATUS_BADGES.NOT_CONNECTED;
    }

    // @ts-expect-error
    return STATUS_BADGES[status];
}

export function getFeatureStatusBanner(
    key: DomainFeatureStatusKey,
    subdomains: SubdomainOption[],
    selectedSubdomain?: SubdomainOption | null,
    hasNewSnippet?: boolean | null
): React.ReactNode | null | undefined {
    if (key === 'bootSnippet' && subdomains.length > 0 && selectedSubdomain && !hasNewSnippet) {
        return (
            <FlashBanner hasBorder={true} type='warning'>
                <div className='full-width flex justify-sb'>
                    <Body>Please embed your tracking snippet</Body>
                    {/* @ts-expect-error */}
                    <Link variant='primary' to='/setup/tracking-snippet' as={RouterLink}>
                        Go to tracking snippet
                    </Link>
                </div>
            </FlashBanner>
        );
    }

    return null;
}

export function getFeatureStatusIcon(key: DomainFeatureStatusKey): React.ComponentType<any> {
    // @ts-expect-error
    return FEATURES[key].icon;
}
